import {
  Check,
  FileSignature,
  LifeBuoy,
  PhoneCall,
  Send,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { Card, CardHead, EmptyState, fmtDuration, fmtMoney, fmtNum } from "../../../ui";
import type { DashFeedItem } from "../../../../lib/dashboard";

const ICON: Record<DashFeedItem["kind"], LucideIcon> = {
  invoice_paid: Check,
  lead: UserPlus,
  campaign: Send,
  call: PhoneCall,
  ticket: LifeBuoy,
  contract: FileSignature,
};

const TINT: Record<DashFeedItem["kind"], string> = {
  invoice_paid: "var(--color-success)",
  lead: "var(--color-primary)",
  campaign: "var(--color-primary-2)",
  call: "var(--color-primary)",
  ticket: "var(--color-warning)",
  contract: "var(--color-success)",
};

/** Minutes for the last hour, then hours, then the date. Nobody needs "1 day
 *  and 4 hours ago" - by then the day is the only part that still matters. */
function ago(iso: string, t: (k: string, v?: Record<string, string | number>) => string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return t("dash.feed.now");
  if (mins < 60) return t("dash.feed.minutes", { n: mins });
  if (mins < 60 * 24) return t("dash.feed.hours", { n: Math.round(mins / 60) });
  return new Date(iso).toLocaleString("et-EE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * What happened, newest first.
 *
 * There is no activity log to read from - this is merged out of the tables that
 * record the events themselves (payments, leads, campaign runs, calls, tickets,
 * signed contracts). That is a feature, not a workaround: an event only appears
 * here once the thing it describes actually happened.
 */
export function FeedCard({ feed }: { feed: DashFeedItem[] }) {
  const t = useT();

  const line = (item: DashFeedItem) => {
    const n = Number(item.detail);
    if (item.kind === "invoice_paid") return t("dash.feed.paid", { who: item.title, sum: fmtMoney(n, true) });
    if (item.kind === "campaign") return t("dash.feed.campaign", { name: item.title, n: fmtNum(n) });
    if (item.kind === "call") return t("dash.feed.call", { who: item.title, dur: fmtDuration(n) });
    if (item.kind === "lead") return t("dash.feed.lead", { who: item.title });
    if (item.kind === "contract") return t("dash.feed.contract", { who: item.title, what: item.detail });
    return t("dash.feed.ticket", { who: item.title, what: item.detail });
  };

  return (
    <Card>
      <CardHead title={t("dash.feed.title")} sub={t("dash.feed.sub")} />
      {feed.length === 0 ? (
        <EmptyState icon={Check} title={t("dash.feed.emptyTitle")} body={t("dash.feed.emptyBody")} />
      ) : (
        <div className="crm-feed">
          {feed.map((item) => {
            const Icon = ICON[item.kind];
            const tint = TINT[item.kind];
            return (
              <div key={item.key} className="crm-fi">
                <span
                  className="crm-fi-d"
                  style={{ background: `color-mix(in oklab, ${tint} 13%, transparent)`, color: tint }}
                >
                  <Icon className="size-3.5" strokeWidth={2} />
                </span>
                <span className="min-w-0">
                  <span className="crm-fi-n block">{line(item)}</span>
                  <span className="crm-fi-t block">{ago(item.at, t)}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
